import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Typography,
  Stack,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  getStoreItemAvailability,
  createItemBlackout,
  deleteItemBlackout
} from '../../api';
import {
  searchProducts,
  searchBundles,
  searchVariants,
  searchModifiers,
  searchOptionValues
} from '../../api/products';
import type {
  ProductSearchResult,
  BundleSearchResult,
  VariantSearchResult,
  ModifierSearchResult,
  OptionValueSearchResult
} from '../../api/products';
import {
  BlackoutItem,
  BlackoutFormData,
  BlackoutType,
  BlackoutReason,
  BlackoutTypeLabels,
  BlackoutReasonLabels,
  getBlackoutType,
  getItemCode,
  getItemDisplayName
} from './types';

interface ItemAvailabilityProps {
  storeNumber: string;
}

const ItemAvailability: React.FC<ItemAvailabilityProps> = ({ storeNumber }) => {
  const [blackouts, setBlackouts] = useState<BlackoutItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedBlackout, setSelectedBlackout] = useState<BlackoutItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Default expiry to 4 AM next day (typical end of operations)
  const getDefault4AM = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(4, 0, 0, 0);
    return tomorrow;
  };

  const [formData, setFormData] = useState<BlackoutFormData>({
    type: 'PRODUCT',
    itemCode: '',
    reason: 'OUT_OF_STOCK',
    expiry: getDefault4AM(),
    startingAt: new Date()
  });

  // Track which quick option is selected for highlighting
  const [selectedQuickOption, setSelectedQuickOption] = useState<string>('endOfDay');

  // Autocomplete search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<(ProductSearchResult | BundleSearchResult | VariantSearchResult | ModifierSearchResult | OptionValueSearchResult)[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Multiple items selection
  const [selectedItems, setSelectedItems] = useState<Array<{
    type: BlackoutType;
    code: string;
    name: string;
  }>>([]);

  // Reset form when dialog opens
  useEffect(() => {
    if (createDialogOpen) {
      setFormData({
        type: 'PRODUCT',
        itemCode: '',
        reason: 'OUT_OF_STOCK',
        expiry: getDefault4AM(),
        startingAt: new Date()
      });
      setSearchQuery('');
      setSearchResults([]);
      setSelectedItems([]);
      setSelectedQuickOption('endOfDay');
    }
  }, [createDialogOpen]);

  const fetchBlackouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStoreItemAvailability(storeNumber, 100);
      setBlackouts(response?.blackouts || []);
    } catch (err) {
      console.error('Failed to fetch snoozed items:', err);
      setError('Failed to load snoozed items');
      setBlackouts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlackouts();
  }, [storeNumber]);

  // Debounced search effect
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (searchQuery.length >= 2) {
      timer = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [searchQuery, formData.type]);

  const performSearch = async (query: string) => {
    setIsSearching(true);
    try {
      let results: (ProductSearchResult | BundleSearchResult | VariantSearchResult | ModifierSearchResult | OptionValueSearchResult)[] = [];

      switch (formData.type) {
        case 'PRODUCT':
          results = await searchProducts(query);
          break;
        case 'BUNDLE':
          results = await searchBundles(query);
          break;
        case 'VARIANT':
          results = await searchVariants(query);
          break;
        case 'MODIFIER':
          results = await searchModifiers(query);
          break;
        case 'OPTION_VALUE':
          results = await searchOptionValues(query);
          break;
        default:
          results = [];
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCreateBlackout = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one item');
      return;
    }

    if (!formData.expiry) {
      setError('Please set an expiry date');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Group items by type
      const productCodes = selectedItems.filter(item => item.type === 'PRODUCT').map(item => item.code);
      const bundleCodes = selectedItems.filter(item => item.type === 'BUNDLE').map(item => item.code);
      const variantCodes = selectedItems.filter(item => item.type === 'VARIANT').map(item => item.code);
      const modifierCodes = selectedItems.filter(item => item.type === 'MODIFIER').map(item => item.code);
      const optionValueCodes = selectedItems.filter(item => item.type === 'OPTION_VALUE').map(item => item.code);

      await createItemBlackout(
        [storeNumber],
        productCodes,
        bundleCodes,
        formData.reason,
        formData.expiry.toISOString(),
        formData.startingAt?.toISOString(),
        variantCodes,
        modifierCodes,
        optionValueCodes
      );
      setCreateDialogOpen(false);
      setFormData({
        type: 'PRODUCT',
        itemCode: '',
        reason: 'OUT_OF_STOCK',
        expiry: getDefault4AM(),
        startingAt: new Date()
      });
      fetchBlackouts();
    } catch (err) {
      console.error('Failed to snooze item:', err);
      setError('Failed to snooze item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBlackout = async () => {
    if (!selectedBlackout) return;

    setSubmitting(true);
    setError(null);

    try {
      const blackoutType = getBlackoutType(selectedBlackout);
      const itemCode = getItemCode(selectedBlackout);

      await deleteItemBlackout(
        storeNumber,
        blackoutType,
        itemCode
      );

      setDeleteConfirmOpen(false);
      setSelectedBlackout(null);

      // Show success message
      setError(null); // Clear any existing errors
      setSuccess(`Successfully activated ${itemCode}`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

      // Refresh the list
      await fetchBlackouts();
    } catch (err: any) {
      console.error('Failed to activate item:', err);
      const errorMessage = err.response?.data?.message ||
                          err.message ||
                          'Failed to activate item';
      setError(errorMessage);
      // Keep dialog open on error so user can retry
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired = (expiry: string) => {
    return new Date(expiry) < new Date();
  };

  const getTimeRemaining = (expiry: string) => {
    const now = new Date();
    const expiryDate = new Date(expiry);
    const diff = expiryDate.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} remaining`;
    }

    return `${hours}h ${minutes}m remaining`;
  };

  return (
    <Card>
      <CardHeader
        title="Item Snoozing"
        subheader="Mark menu items as temporarily unavailable (snoozed)"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchBlackouts} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading}
            >
              Snooze an Item
            </Button>
          </Box>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : blackouts.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No items are currently snoozed
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blackouts.map((blackout, index) => {
                  const type = getBlackoutType(blackout);
                  const expired = isExpired(blackout.expiry);

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={BlackoutTypeLabels[type]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getItemDisplayName(blackout)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={BlackoutReasonLabels[blackout.reason]}
                          size="small"
                          color={blackout.reason === 'OUT_OF_STOCK' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        {format(new Date(blackout.startingAt), 'MMM d, h:mm a')}
                      </TableCell>
                      <TableCell>
                        {format(new Date(blackout.expiry), 'MMM d, h:mm a')}
                      </TableCell>
                      <TableCell>
                        {expired ? (
                          <Chip label="Expired" size="small" color="default" />
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {getTimeRemaining(blackout.expiry)}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBlackout(blackout);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Create Snooze Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          // Reset form and search state
          setFormData({
            type: 'PRODUCT',
            itemCode: '',
            reason: 'OUT_OF_STOCK',
            expiry: null,
            startingAt: new Date()
          });
          setSearchQuery('');
          setSearchResults([]);
          setSelectedItems([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Snooze Menu Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Type"
              select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as BlackoutType }))}
              fullWidth
            >
              {Object.entries(BlackoutTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>

            {/* Autocomplete for all types - Multi-select */}
            <Autocomplete
              multiple
              value={selectedItems.map(item => ({
                code: item.code,
                name: item.name,
                label: `${item.code} - ${item.name}`
              }))}
              onChange={(_, newValue) => {
                const updatedItems = newValue.map(item => ({
                  type: formData.type,
                  code: item.code,
                  name: item.name
                }));
                setSelectedItems(updatedItems);
              }}
              inputValue={searchQuery}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setSearchQuery(newInputValue);
                }
              }}
              options={searchResults}
              getOptionLabel={(option) => option.label || `${option.code} - ${option.name}`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              disableCloseOnSelect
              loading={isSearching}
              loadingText="Searching..."
              noOptionsText={searchQuery.length < 2 ? `Type to search ${BlackoutTypeLabels[formData.type]}` : 'No results found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item Code*"
                  placeholder={selectedItems.length === 0 ? `Search ${BlackoutTypeLabels[formData.type]}s...` : ''}
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.code}
                    label={`${option.code} - ${option.name}`}
                    size="small"
                  />
                ))
              }
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props as any;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Checkbox
                      checked={selected}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body1">{option.label || `${option.code} - ${option.name}`}</Typography>
                  </Box>
                );
              }}
            />

            <TextField
              label="Reason"
              select
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value as BlackoutReason }))}
              fullWidth
            >
              {Object.entries(BlackoutReasonLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Starts At"
                value={formData.startingAt}
                onChange={(newValue) => setFormData(prev => ({ ...prev, startingAt: newValue || new Date() }))}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />

              <DateTimePicker
                label="Expires At"
                value={formData.expiry}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, expiry: newValue }));
                  setSelectedQuickOption('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: formData.expiry ? `Reactivates: ${format(formData.expiry, 'MMM d, h:mm a')}` : null
                  }
                }}
                minDateTime={formData.startingAt || new Date()}
              />
            </LocalizationProvider>

            {/* Quick Expiry Options */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick Options
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {/* Hourly options */}
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  <Chip
                    label="1 H"
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setHours(expiry.getHours() + 1);
                      setFormData(prev => ({ ...prev, expiry }));
                      setSelectedQuickOption('1h');
                    }}
                    variant={selectedQuickOption === '1h' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '1h' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="2 H"
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setHours(expiry.getHours() + 2);
                      setFormData(prev => ({ ...prev, expiry }));
                      setSelectedQuickOption('2h');
                    }}
                    variant={selectedQuickOption === '2h' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '2h' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="4 H"
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setHours(expiry.getHours() + 4);
                      setFormData(prev => ({ ...prev, expiry }));
                      setSelectedQuickOption('4h');
                    }}
                    variant={selectedQuickOption === '4h' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '4h' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="6 H"
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setHours(expiry.getHours() + 6);
                      setFormData(prev => ({ ...prev, expiry }));
                      setSelectedQuickOption('6h');
                    }}
                    variant={selectedQuickOption === '6h' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '6h' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="12 H"
                    onClick={() => {
                      const expiry = new Date();
                      expiry.setHours(expiry.getHours() + 12);
                      setFormData(prev => ({ ...prev, expiry }));
                      setSelectedQuickOption('12h');
                    }}
                    variant={selectedQuickOption === '12h' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '12h' ? 'primary' : 'default'}
                    size="small"
                  />
                </Stack>

                {/* Daily options */}
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  <Chip
                    label="End of Day (4 AM)"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, expiry: getDefault4AM() }));
                      setSelectedQuickOption('endOfDay');
                    }}
                    variant={selectedQuickOption === 'endOfDay' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === 'endOfDay' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="Tonight (11:59 PM)"
                    onClick={() => {
                      const tonight = new Date();
                      tonight.setHours(23, 59, 59, 999);
                      setFormData(prev => ({ ...prev, expiry: tonight }));
                      setSelectedQuickOption('tonight');
                    }}
                    variant={selectedQuickOption === 'tonight' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === 'tonight' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="Tomorrow"
                    onClick={() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      tomorrow.setHours(new Date().getHours(), new Date().getMinutes(), 0, 0);
                      setFormData(prev => ({ ...prev, expiry: tomorrow }));
                      setSelectedQuickOption('tomorrow');
                    }}
                    variant={selectedQuickOption === 'tomorrow' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === 'tomorrow' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="1 Week"
                    onClick={() => {
                      const nextWeek = new Date();
                      nextWeek.setDate(nextWeek.getDate() + 7);
                      setFormData(prev => ({ ...prev, expiry: nextWeek }));
                      setSelectedQuickOption('1week');
                    }}
                    variant={selectedQuickOption === '1week' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '1week' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label="1 Month"
                    onClick={() => {
                      const nextMonth = new Date();
                      nextMonth.setMonth(nextMonth.getMonth() + 1);
                      setFormData(prev => ({ ...prev, expiry: nextMonth }));
                      setSelectedQuickOption('1month');
                    }}
                    variant={selectedQuickOption === '1month' ? 'filled' : 'outlined'}
                    color={selectedQuickOption === '1month' ? 'primary' : 'default'}
                    size="small"
                  />
                </Stack>
              </Stack>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            // Reset form and search state
            setFormData({
              type: 'PRODUCT',
              itemCode: '',
              reason: 'OUT_OF_STOCK',
              expiry: getDefault4AM(),
              startingAt: new Date()
            });
            setSearchQuery('');
            setSearchResults([]);
            setSelectedItems([]);
          }} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateBlackout}
            variant="contained"
            disabled={submitting || selectedItems.length === 0 || !formData.expiry}
          >
            {submitting ? <CircularProgress size={20} /> :
             selectedItems.length > 0 ? `Snooze Items (${selectedItems.length})` : 'Snooze Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Activate Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to activate this item? It will become available immediately.
          </Typography>
          {selectedBlackout && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Type: {BlackoutTypeLabels[getBlackoutType(selectedBlackout)]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Item Code: {getItemCode(selectedBlackout)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reason: {BlackoutReasonLabels[selectedBlackout.reason]}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBlackout}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ItemAvailability;